import React from 'react';
import styled, { css } from 'styled-components';
import { Intent } from '@blueprintjs/core';

import Button from './Button';
import Box from './Box';
import type { BoxProps } from './types';

export interface SegmentedControlOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

export interface SegmentedControlProps extends Omit<BoxProps, 'onChange'> {
  options: SegmentedControlOption[];
  /** Currently selected value (controlled). */
  value?: string;
  onChange?: (value: string) => void;
  /** Visual intent applied to the selected segment. */
  intent?: Intent;
  fill?: boolean;
  inline?: boolean;
  small?: boolean;
  large?: boolean;
  disabled?: boolean;
  role?: 'menu' | 'toolbar' | 'group' | 'radiogroup';
}

const SegmentedControlContainer = styled(Box)<{ fill?: boolean; inline?: boolean }>`
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  gap: 3px;
  padding: 3px;
  border-radius: 20px;

  // same as the Button active state, but applied to the container to create a "connected" look
  background-color: ${({ theme }) => (theme.name === 'dark' ? '#3e3e3e' : '#f6f7f9')};

  ${({ fill }) =>
    fill &&
    css`
      width: 100%;

      > .bp4-button {
        flex-grow: 1;
      }
    `}

  /* all segments are pill-shaped */
  > .bp4-button {
    border-radius: 20px;
  }

  /* selected (non-minimal) segment gets a solid surface */
  > .bp4-button:not(.bp4-minimal):not(.bp4-intent-primary) {
    background-color: ${({ theme }) => (theme.name === 'dark' ? 'rgb(37, 37, 37)' : '#ffffff')};
    box-shadow: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgb(255 255 255 / 8%) 0px 0px 0px 1px, rgb(0 0 0 / 0%) 0px 1px 1px'
        : '0 0 0 1px rgba(17, 20, 24, 0.1), 0 1px 1px rgba(17, 20, 24, 0.12)'};
  }

  /* unselected segments are muted */
  > .bp4-button.bp4-minimal {
    color: ${({ theme }) => (theme.name === 'dark' ? 'rgba(171, 179, 191, 1)' : 'rgba(95, 107, 124, 1)')};
  }

  > .bp4-button.bp4-minimal:disabled {
    color: ${({ theme }) => (theme.name === 'dark' ? 'rgba(171, 179, 191, 0.6)' : 'rgba(95, 107, 124, 0.6)')};
  }
`;

const SegmentedControl = ({
  options,
  value,
  onChange,
  fill,
  inline,
  small,
  large,
  disabled,
  role = 'radiogroup',
  ...boxProps
}: SegmentedControlProps) => (
  <SegmentedControlContainer fill={fill} inline={inline} role={role} {...boxProps}>
    {options.map((option) => {
      const isActive = value === option.value;
      return (
        <Button
          key={option.value}
          text={option.label}
          minimal={!isActive}
          active={isActive}
          small={small}
          large={large}
          disabled={disabled || option.disabled}
          onClick={() => onChange?.(option.value)}
          aria-checked={isActive}
          role={role === 'radiogroup' ? 'radio' : 'button'}
        />
      );
    })}
  </SegmentedControlContainer>
);

export default SegmentedControl;
